import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import api from "../../api";

/** yyyy-mm-dd for the native date inputs. */
const toInput = (date) => date.toISOString().slice(0, 10);

const weeksAgo = (weeks) => {
  const d = new Date();
  d.setDate(d.getDate() - weeks * 7);
  return d;
};

const SKILL_COLOR = {
  outstanding: "success",
  keep_practicing: "warning",
  needs_improvement: "error",
};

const Tile = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 110, textAlign: "center" }}>
    <Typography variant="h6" fontWeight={800}>
      {value ?? "—"}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Paper>
);

/**
 * Preview and send a term progress report for one student.
 *
 * The tutor picks the window, reads what the parent would see, edits the AI paragraph if they
 * want to, and sends. Preview is explicit rather than automatic on every keystroke — each one
 * builds a digest server-side.
 */
const ProgressReportDialog = ({ open, buyerId, studentName, onClose }) => {
  const [from, setFrom] = useState(toInput(weeksAgo(12)));
  const [to, setTo] = useState(toInput(new Date()));
  const [preview, setPreview] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [duplicate, setDuplicate] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setNarrative("");
    setError(null);
    setDuplicate(false);
  }, [open]);

  const loadPreview = useCallback(
    async (regenerate = false) => {
      try {
        setLoading(true);
        setError(null);
        setDuplicate(false);
        const res = await api.post("tutor/progress-reports/preview", {
          student_id: Number(buyerId),
          period_start: from,
          period_end: to,
          regenerate,
        });
        const data = res.data?.data;
        setPreview(data);
        setNarrative(data?.narrative ?? "");
      } catch (err) {
        setPreview(null);
        setError(err.response?.data?.message || "Could not build the preview.");
      } finally {
        setLoading(false);
      }
    },
    [buyerId, from, to]
  );

  const send = async (force = false) => {
    try {
      setSending(true);
      setError(null);
      const res = await api.post("tutor/progress-reports/send", {
        student_id: Number(buyerId),
        period_start: from,
        period_end: to,
        narrative: narrative.trim() ? narrative.trim() : null,
        force,
      });
      toast.success(res.data?.message || "Progress report sent.");
      onClose?.();
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        // Already sent for this window — offer "send anyway" rather than calling it a failure.
        setDuplicate(true);
        setError(err.response?.data?.message || "A report for this period has already been sent.");
      } else {
        setError(err.response?.data?.message || "Could not send the report.");
      }
    } finally {
      setSending(false);
    }
  };

  const overview = preview?.digest?.overview || {};
  const recipient = preview?.recipient;
  const busy = loading || sending;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        Send progress report
        <Typography variant="body2" color="text.secondary">
          {studentName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button onClick={() => loadPreview(false)} disabled={busy} variant="outlined">
            {loading ? "Building…" : "Preview"}
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            size="small"
            label="Last 4 weeks"
            onClick={() => {
              setFrom(toInput(weeksAgo(4)));
              setTo(toInput(new Date()));
            }}
          />
          <Chip
            size="small"
            label="Last 12 weeks"
            onClick={() => {
              setFrom(toInput(weeksAgo(12)));
              setTo(toInput(new Date()));
            }}
          />
        </Stack>

        {error && (
          <Alert
            severity={duplicate ? "warning" : "error"}
            sx={{ mb: 2 }}
            action={
              duplicate ? (
                <Button size="small" color="inherit" onClick={() => send(true)} disabled={sending}>
                  Send anyway
                </Button>
              ) : null
            }
          >
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && !preview && !error && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            Choose a date range and press Preview to see what the parent would receive.
          </Typography>
        )}

        {!loading && preview && (
          <>
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
              <Tile label="Completed" value={overview.completions} />
              <Tile
                label="Average score"
                value={overview.average_score != null ? `${overview.average_score}%` : "—"}
              />
              <Tile label="Time spent" value={overview.time_spent_label} />
            </Stack>

            {preview.digest?.skill_areas?.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                {preview.digest.skill_areas.map((area) => (
                  <Chip
                    key={area.key || area.name}
                    size="small"
                    label={`${area.name}: ${area.average_score != null ? `${area.average_score}%` : "—"}`}
                    color={SKILL_COLOR[area.status] || "default"}
                    variant={SKILL_COLOR[area.status] ? "filled" : "outlined"}
                  />
                ))}
              </Stack>
            )}

            {preview.digest?.weak_topics?.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Areas to improve: {preview.digest.weak_topics.map((t) => t.name).join(", ")}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Note to the parent
              </Typography>
              <Button
                size="small"
                startIcon={<AutorenewIcon />}
                onClick={() => loadPreview(true)}
                disabled={busy}
              >
                Regenerate
              </Button>
            </Stack>

            {preview.narrative_source === "none" && !narrative && (
              <Alert severity="info" sx={{ mb: 1 }}>
                The AI summary is unavailable right now — the report will send with the figures
                only, or you can write your own note below.
              </Alert>
            )}

            <TextField
              value={narrative}
              onChange={(e) => setNarrative(e.target.value.slice(0, 2000))}
              fullWidth
              multiline
              minRows={4}
              placeholder="A short paragraph for the parent…"
              helperText={`${narrative.length}/2000 — edit freely; your wording is what gets sent.`}
            />

            {recipient && (
              <Alert severity={recipient.email ? "info" : "warning"} sx={{ mt: 2 }}>
                {recipient.email
                  ? `Will be emailed to ${recipient.name} at ${recipient.email}${
                      recipient.is_parent ? " (parent)" : " (student)"
                    }.`
                  : "No email address on file for this student — the report cannot be sent."}
              </Alert>
            )}

            {preview.suppressed && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This recipient has opted out of progress emails.
              </Alert>
            )}

            {preview.already_sent_at && !duplicate && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                A report for this exact period was already sent.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => send(false)}
          disabled={busy || !preview || !recipient?.email || preview.suppressed}
          variant="contained"
          startIcon={<SendIcon />}
        >
          {sending ? "Sending…" : "Send to parent"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProgressReportDialog;
